import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addStyleExample } from '../../api/saenggibuApi';
import { useToast } from '../Toast';
import { trackSaenggibuUxEvent } from '../../utils/eventTracker';
import {
  STYLE_EXAMPLE_COPY,
  mapSaenggibuCategoryToStyleScope,
} from '../../utils/saenggibuStyleExamples';

/**
 * 결과 문장을 '내 스타일 참고 예시'로 저장하는 동작.
 * 버튼(SaveAsStyleExampleButton)과 더보기 메뉴 항목이 같은 로직·토스트·이벤트를 공유한다.
 *
 * @returns {{ available: boolean, saving: boolean, save: (event?: Event) => Promise<void>, label: string }}
 */
export default function useSaveAsStyleExample({
  text,
  category,
  categoryHint,
  wasEdited = false,
  isPro = false,
  isBatch = false,
  versionIndex,
  disabled = false,
} = {}) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const trimmed = String(text || '').trim();
  const available = Boolean(isPro && trimmed);

  const save = async (event) => {
    event?.stopPropagation?.();
    if (!available || saving || disabled) return;

    const scope = mapSaenggibuCategoryToStyleScope(category, categoryHint);
    const source = wasEdited ? 'from_edit' : 'from_output';

    try {
      setSaving(true);
      const data = await addStyleExample(scope, {
        text: trimmed,
        category: scope === 'global' ? null : scope,
        source,
      });
      trackSaenggibuUxEvent('saenggibu_example_saved', {
        saenggibu_category: scope,
        is_batch: isBatch,
        source,
        version_index: versionIndex,
      });

      if (data?.duplicate) {
        showToast(STYLE_EXAMPLE_COPY.duplicateToast, 'info');
        return;
      }

      const savedText = String(data?.example?.text || '');
      const truncated = savedText && savedText.length < trimmed.length;
      showToast(
        truncated ? STYLE_EXAMPLE_COPY.truncatedToast : STYLE_EXAMPLE_COPY.savedToast,
        'success',
        5000,
        {
          label: STYLE_EXAMPLE_COPY.savedAction,
          onClick: () => navigate('/saenggibu-my-style'),
        }
      );
    } catch (error) {
      const message = String(error?.detail || error?.message || '저장하지 못했어요. 다시 시도해주세요');
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return {
    available,
    saving,
    save,
    label: saving ? '저장 중…' : STYLE_EXAMPLE_COPY.saveButton,
  };
}
